import React from 'react';
import {
  Box, Typography, Slider, FormControlLabel, Switch, Checkbox, Select, MenuItem,
  FormControl, InputLabel, Divider, Tooltip, IconButton, TextField,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { BarcodeOptions } from './types';

interface BarcodeSettingsProps {
  options: BarcodeOptions;
  onChange: (options: BarcodeOptions) => void;
}

const GS1_STANDARD_HEIGHT = 22.85;

const SC_SIZES = [
  { label: 'SC0 (80%)', value: 80 },
  { label: 'SC1 (90%)', value: 90 },
  { label: 'SC2 (100%) – Standard', value: 100 },
  { label: 'SC3 (110%)', value: 110 },
  { label: 'SC4 (120%)', value: 120 },
  { label: 'SC5 (130%)', value: 130 },
  { label: 'SC6 (150%)', value: 150 },
  { label: 'SC7 (170%)', value: 170 },
  { label: 'SC8 (185%)', value: 185 },
  { label: 'SC9 (200%)', value: 200 },
];

const BarcodeSettings: React.FC<BarcodeSettingsProps> = ({ options, onChange }) => {
  const update = (partial: Partial<BarcodeOptions>) => {
    onChange({ ...options, ...partial });
  };

  const mag = options.magnification / 100;
  const xDimMm = 0.330 * mag;
  const barcodeWidthMm = 31.35 * mag;
  const totalWidthMm = 37.29 * mag;
  const croppedWidthMm = Math.max(0, totalWidthMm - (options.cropLeft || 0));
  const actualBarHeight = options.barHeight * mag;
  const guardExtMm = 5 * 0.330 * mag;
  const totalHeightMm = actualBarHeight + guardExtMm;

  const isGS1Height = Math.abs(options.barHeight - GS1_STANDARD_HEIGHT) < 0.01;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Inställningar
      </Typography>

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Storlek (SC)</InputLabel>
        <Select
          value={options.magnification}
          label="Storlek (SC)"
          onChange={(e) => update({ magnification: Number(e.target.value) })}
        >
          {SC_SIZES.map((sc) => (
            <MenuItem key={sc.value} value={sc.value}>{sc.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isGS1Height}
              onChange={(_, checked) => {
                if (checked) update({ barHeight: GS1_STANDARD_HEIGHT });
              }}
              size="small"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: isGS1Height ? 600 : 400 }}>
              GS1 rekommenderad höjd{' '}
              <Typography
                component="a"
                variant="caption"
                href="https://gs1.se/guider/kom-igang-guider/storleksguide/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                (se regler)
              </Typography>
            </Typography>
          }
          sx={{ mb: 0.5 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', minWidth: 90 }}>
            Streckhöjd:
          </Typography>
          <Slider
            value={options.barHeight}
            min={10}
            max={35}
            step={0.05}
            onChange={(_, v) => update({ barHeight: v as number })}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            value={options.barHeight.toFixed(2)}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v) && v >= 10 && v <= 50) update({ barHeight: v });
            }}
            size="small"
            sx={{ width: 80 }}
            InputProps={{
              endAdornment: <Typography variant="caption" color="text.secondary">mm</Typography>,
              sx: { fontSize: '0.85rem' },
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Faktisk höjd efter skalning: <strong>{actualBarHeight.toFixed(2)} mm</strong>
        </Typography>
      </Box>

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>DPI (upplösning)</InputLabel>
        <Select
          value={options.dpi}
          label="DPI (upplösning)"
          onChange={(e) => update({ dpi: Number(e.target.value) })}
        >
          <MenuItem value={300}>300 DPI</MenuItem>
          <MenuItem value={600}>600 DPI – Rekommenderad</MenuItem>
          <MenuItem value={1200}>1200 DPI</MenuItem>
        </Select>
      </FormControl>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', minWidth: 90 }}>
            Beskär vänster:
          </Typography>
          <Slider
            value={options.cropLeft}
            min={0}
            max={5}
            step={0.1}
            onChange={(_, v) => update({ cropLeft: v as number })}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            value={options.cropLeft.toFixed(1)}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v) && v >= 0 && v <= 10) update({ cropLeft: v });
            }}
            size="small"
            sx={{ width: 80 }}
            InputProps={{
              endAdornment: <Typography variant="caption" color="text.secondary">mm</Typography>,
              sx: { fontSize: '0.85rem' },
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Beskär bort pixlar från vänster kant
        </Typography>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <FormControlLabel
        control={<Switch checked={options.showText} onChange={(_, v) => update({ showText: v })} size="small" />}
        label={<Typography variant="body2">Visa siffror (human-readable)</Typography>}
        sx={{ mb: 0.5 }}
      />

      <FormControlLabel
        control={<Switch checked={options.showQuietZones} onChange={(_, v) => update({ showQuietZones: v })} size="small" />}
        label={<Typography variant="body2">Inkludera tysta zoner</Typography>}
        sx={{ mb: 0.5 }}
      />

      <FormControlLabel
        control={<Switch checked={options.lightMarginIndicator} onChange={(_, v) => update({ lightMarginIndicator: v })} size="small" />}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">Light margin indicator (&gt;)</Typography>
            <Tooltip title="Visar '>' symbolen som markerar var den tysta zonen slutar. GS1-rekommendation.">
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </Tooltip>
          </Box>
        }
        sx={{ mb: 0.5 }}
      />

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
          Fysiska mått
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px 12px' }}>
          <Typography variant="caption" color="text.secondary">X-dimension:</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>{xDimMm.toFixed(3)} mm</Typography>

          <Typography variant="caption" color="text.secondary">Streckbredd (exkl. tysta zoner):</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>{barcodeWidthMm.toFixed(2)} mm</Typography>

          <Typography variant="caption" color="text.secondary">Total bredd (före beskärning):</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>{totalWidthMm.toFixed(2)} mm</Typography>

          {(options.cropLeft || 0) > 0 && (<>
            <Typography variant="caption" color="text.secondary">Bredd efter beskärning:</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right', color: 'primary.main' }}>{croppedWidthMm.toFixed(2)} mm</Typography>
          </>)}

          <Typography variant="caption" color="text.secondary">Streckhöjd:</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>{actualBarHeight.toFixed(2)} mm</Typography>

          <Typography variant="caption" color="text.secondary">Total höjd (inkl. guard):</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>{totalHeightMm.toFixed(2)} mm</Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
          Tips
        </Typography>
        <Typography variant="caption" color="text.secondary">
          • TIFF-filen är 1-bit bitmap och kan färgas i InDesign
          <br />
          • Minsta storlek för DVH: SC2 (100%). Flexo: SC4 (120%)+
        </Typography>
      </Box>
    </Box>
  );
};

export default BarcodeSettings;
